import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import { KineticText } from "@/components/KineticText";
import Reveal from "@/components/Reveal";
import Stats from "@/components/Stats";
import EditorialMarquee from "@/components/EditorialMarquee";
import { BUSINESS, IMAGES, PRODUCTS, STATS, WHY, TESTIMONIALS, FAQS } from "@/lib/content";

const waHref = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(
  "Hello, I would like to enquire about wholesale vegetable supply."
)}`;

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Sunrise over agricultural fields" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-brand-forest-dark" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-dark/90 via-brand-forest/30 to-transparent" />

      <div className="relative z-10 h-full max-w-screen-2xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs md:text-sm uppercase tracking-[0.28em] text-brand-leaf-light mb-6"
        >
          Karnavati Agriculture Market Yard · Vatva · Ahmedabad
        </motion.span>

        <KineticText
          as="h1"
          lines={["Pravin Hiralal", "Prajapati"]}
          delay={0}
          className="font-display text-white text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight"
        />

        <div
          className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <div>
            <p className="text-white/90 text-lg md:text-2xl font-light max-w-xl leading-snug">
              {BUSINESS.tagline}
            </p>
            <p className="font-display italic text-brand-leaf-light text-xl md:text-2xl mt-1">
              {BUSINESS.taglineGu}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-leaf text-white px-7 py-4 font-medium hover:bg-brand-leaf-light transition-colors duration-300 active:scale-95"
            >
              Chat on WhatsApp <ArrowUpRight size={18} />
            </a>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-4 font-medium hover:bg-white hover:text-brand-forest transition-colors duration-300 active:scale-95"
            >
              <Phone size={17} /> Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section className="py-24 md:py-36 max-w-screen-2xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div ref={ref} className="relative order-2 lg:order-1">
          <div className="overflow-hidden rounded-t-[10rem] rounded-b-3xl aspect-[4/5] shadow-xl">
            <motion.img
              style={{ y, scale: 1.15 }}
              src={IMAGES.hands}
              alt="Hands preparing fresh soil"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-4 md:right-8 bg-white rounded-2xl px-7 py-5 shadow-lg border border-brand-forest/5">
            <span className="font-display text-4xl text-brand-forest">Since dawn</span>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted mt-1">Fresh graded daily</p>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">Chapter 01</span>
            <h2 className="font-display text-4xl md:text-6xl text-brand-forest tracking-tight leading-none mt-4">
              From the soil to your market — a promise kept daily.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-brand-muted text-lg leading-relaxed max-w-lg">
              For years, we have connected trusted farmers with wholesalers, retailers, hotels, and exporters —
              supplying fresh, quality-graded vegetables with honest weighing and transparent pricing every single day.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/about"
              data-testid="about-teaser-link"
              className="group inline-flex items-center gap-2 mt-10 font-medium text-brand-forest cursor-hover"
            >
              Read our story
              <span className="h-10 w-10 rounded-full border border-brand-forest/20 flex items-center justify-center group-hover:bg-brand-forest group-hover:text-white transition-colors duration-300">
                <ArrowRight size={16} />
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ProductsGlance() {
  const featured = PRODUCTS.slice(0, 3);
  return (
    <section className="py-24 md:py-32 bg-brand-sand grain relative">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">What we deal in</span>
            <h2 className="font-display text-4xl md:text-6xl text-brand-forest tracking-tight mt-3">Fresh. Premium. Daily.</h2>
          </div>
          <Link to="/products" data-testid="glance-all-products" className="text-brand-forest font-medium inline-flex items-center gap-2 cursor-hover">
            View all products <ArrowRight size={16} />
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <Link to="/products" className="group block">
                <div className="overflow-hidden rounded-t-[8rem] rounded-b-3xl bg-white aspect-[3/4]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="flex items-baseline justify-between mt-5">
                  <h3 className="font-display text-2xl text-brand-forest">{p.name.replace("Fresh ", "")}</h3>
                  <span className="font-display italic text-brand-leaf text-lg">{p.nameGu}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12">
      <Reveal className="mb-16 max-w-2xl">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">By the numbers</span>
        <h2 className="font-display text-4xl md:text-5xl text-brand-forest mt-3">A network built on trust and freshness.</h2>
      </Reveal>
      <Stats items={STATS} />
    </section>
  );
}

function WhyUs() {
  return (
    <section className="py-24 md:py-32 bg-brand-forest text-brand-bone relative grain overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal className="mb-16 max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-leaf-light">Why choose us</span>
          <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tight">Honest trading, every single lot.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-bone/10 rounded-3xl overflow-hidden">
          {WHY.map((w, i) => (
            <Reveal key={w.n} delay={i * 0.08} className="bg-brand-forest">
              <div className="p-8 lg:p-10 h-full hover:bg-brand-forest-light transition-colors duration-500">
                <span className="font-mono text-brand-terra text-sm">{w.n}</span>
                <h3 className="font-display text-3xl mt-5">{w.title}</h3>
                <p className="text-brand-bone/60 mt-4 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12">
      <Reveal className="mb-16">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">Trusted by buyers</span>
        <h2 className="font-display text-4xl md:text-6xl text-brand-forest mt-3 tracking-tight">Words from the market.</h2>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <figure data-testid={`testimonial-${i}`} className="bg-white rounded-3xl p-8 border border-brand-forest/5 h-full flex flex-col hover:shadow-xl transition-shadow duration-500">
              <span className="font-display text-7xl text-brand-leaf/30 leading-none">&ldquo;</span>
              <blockquote className="text-brand-forest text-lg leading-relaxed -mt-4 flex-1">{t.quote}</blockquote>
              <figcaption className="mt-6 pt-6 border-t border-brand-forest/10">
                <div className="font-medium text-brand-forest">{t.name}</div>
                <div className="font-mono text-xs uppercase tracking-wide text-brand-muted mt-1">{t.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="py-24 md:py-32 bg-brand-sand relative grain">
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal className="mb-12 text-center">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-terra">FAQ</span>
          <h2 className="font-display text-4xl md:text-6xl text-brand-forest mt-3 tracking-tight">Common questions.</h2>
        </Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-brand-forest/15">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-xl md:text-2xl text-brand-forest py-7 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-muted text-base leading-relaxed pb-7">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function CTABand() {
  return (
    <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12">
      <div className="rounded-[2.5rem] bg-brand-forest text-brand-bone p-10 md:p-20 relative overflow-hidden grain">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-2xl">
            Ready to source fresh vegetables in bulk?
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="cta-enquiry-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-leaf text-white px-8 py-4 font-medium hover:bg-brand-leaf-light transition-colors duration-300 active:scale-95">
              Send an Enquiry <ArrowUpRight size={18} />
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-8 py-4 font-medium hover:bg-white hover:text-brand-forest transition-colors duration-300 active:scale-95">
              <Phone size={17} /> Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Wholesale Vegetable Supplier & Commission Agent"
        description="Pravin Hiralal Prajapati — trusted wholesale vegetable supplier & commission agent at Karnavati Market Yard, Vatva, Ahmedabad. Fresh capsicum, cucumber, green peas, cabbage & carrot in bulk."
        path=""
      />
      <Hero />
      <EditorialMarquee />
      <AboutTeaser />
      <ProductsGlance />
      <StatsBand />
      <WhyUs />
      <Testimonials />
      <FAQ />
      <CTABand />
    </>
  );
}
